import { ColumnDef } from "@tanstack/react-table";
import { EyeIcon, TrashAltIcon } from "assets/icons";
import cn from "classnames";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import moment from "moment";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ItemsTable, TableItemActionsControl } from "ui";

import { ETransferStatus } from "../../constants";
import {
  formatTransferRef,
  getTransferActorName,
  getTransferDirectionZones,
} from "../../helpers";
import { Transfer } from "../../transfers.types";
import { TransfersTableProps } from "./TransfersTable.types";

import styles from "./TransfersTable.module.scss";

const TransfersTable = ({
  transfers,
  isLoading,
  sorting,
  setSorting,
  onCancelTransfer,
}: TransfersTableProps) => {
  const navigate = useNavigate();
  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const columns: Array<ColumnDef<Transfer>> = useMemo(
    () => [
      {
        id: "ref",
        header: t("Reference"),
        enableSorting: false,
        size: 14,
        minSize: 14,
        cell: ({ row }) => (
          <span className={styles.ref}>
            {formatTransferRef(row.original.id)}
          </span>
        ),
      },
      {
        header: t("Direction"),
        enableSorting: false,
        size: 28,
        minSize: 28,
        cell: ({ row }) => {
          const { from, to } = getTransferDirectionZones(
            row.original.direction,
          );
          return (
            <span className={styles.direction}>
              {t(from)}
              <span className={styles.direction_arrow}>→</span>
              {t(to)}
            </span>
          );
        },
      },
      {
        header: t("Items"),
        enableSorting: false,
        size: 10,
        minSize: 10,
        cell: ({ row }) =>
          row.original.itemsCount ?? row.original.items?.length ?? 0,
      },
      {
        header: t("Created by"),
        enableSorting: false,
        size: 18,
        minSize: 18,
        cell: ({ row }) => getTransferActorName(row.original),
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("Created"),
        size: 16,
        minSize: 16,
        cell: ({ row }) =>
          moment(row.original.createdAt).format("DD MMM YYYY, HH:mm"),
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("Status"),
        size: 10,
        minSize: 10,
        cell: ({ row }) => (
          <span
            className={cn(
              styles.status,
              styles[`status__${row.original.status}`],
            )}
          >
            {t(row.original.status)}
          </span>
        ),
      },
      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 4,
        minSize: 4,
        enableSorting: false,
        cell: ({ row }) => {
          const canCancel =
            row.original.status === ETransferStatus.Current ||
            row.original.status === ETransferStatus.Pending;

          return (
            <div
              className={styles.actions}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <TableItemActionsControl
                actionsList={[
                  {
                    label: "View",
                    Icon: <EyeIcon fill="#090914" width={18} height={18} />,
                    onClick: () =>
                      navigate(`/warehouse/transfers/${row.original.id}`),
                  },
                  ...(canCancel
                    ? [
                        {
                          label: "Cancel transfer",
                          Icon: (
                            <TrashAltIcon
                              fill="#ff0033"
                              width={18}
                              height={18}
                            />
                          ),
                          labelColor: "#ff0033",
                          onClick: () => onCancelTransfer(row.original),
                        },
                      ]
                    : []),
                ]}
                rowIndex={row.index}
                setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
                openedItemOptionsIndex={openedItemOptionsIndex}
              />
            </div>
          );
        },
      },
    ],
    [transfers, openedItemOptionsIndex],
  );

  return (
    <ItemsTable
      columns={columns}
      data={transfers}
      sorting={sorting}
      skeletonHeight={56}
      setSorting={setSorting}
      isLoading={isLoading}
      rowsClassName={styles.tallRow}
      emptyListMessage="No transfers yet"
      getLinkUrl={(transfer) => `/warehouse/transfers/${transfer.id}`}
    />
  );
};

export default TransfersTable;
